import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { MutationConfig, queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { TChannel } from '../../_shared/types/channel.type';
import {
  GET_LIST_INTEGRATION_YOUTUBE_QUERY_KEY,
  TGetListIntegrationYoutubeResponse,
} from './get-list-integration-youtube.api';

/**
 * Delete Integration Youtube Input Schema
 */
export const deleteIntegrationYoutubeValidationSchema = z.object({
  organizationChannelId: z.string({ required_error: 'Organization channel id is required' }),
});
export type TDeleteIntegrationYoutubeValidation = z.infer<typeof deleteIntegrationYoutubeValidationSchema>;
export type TDeleteIntegrationYoutubePayload = TDeleteIntegrationYoutubeValidation;
export type TDeleteIntegrationYoutubeResponse = ApiResponse<TChannel>;

export const deleteIntegrationYoutube = async (
  payload: TDeleteIntegrationYoutubePayload
): Promise<TDeleteIntegrationYoutubeResponse> => {
  const url = `/integration/youtube/org-channel/${payload.organizationChannelId}`;

  return axiosInstance.delete(url);
};

/**
 * Use Delete Integration Youtube React-Query Hook
 */
type UseDeleteIntegrationYoutubeOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteIntegrationYoutube, AxiosErrorCustom>;
};

export const useDeleteIntegrationYoutube = ({ mutationConfig }: UseDeleteIntegrationYoutubeOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteIntegrationYoutube,
    ...restConfig,
  });
};

/**
 * Use Delete Integration Youtube Optimistically React-Query Hook
 */
type UseDeleteIntegrationYoutubeOptimisticallyOptions = {
  mutationConfig?: MutationConfig<typeof deleteIntegrationYoutube>;
};

type TDeleteIntegrationYoutubeOptimisticallyContext = {
  previousData: TGetListIntegrationYoutubeResponse | undefined;
};

export const useDeleteIntegrationYoutubeOptimistically = ({
  mutationConfig,
}: UseDeleteIntegrationYoutubeOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteIntegrationYoutube,
    onMutate: async (data: TDeleteIntegrationYoutubeValidation) => {
      await queryClient.cancelQueries({ queryKey: [GET_LIST_INTEGRATION_YOUTUBE_QUERY_KEY] });

      const previousData = queryClient.getQueryData<TGetListIntegrationYoutubeResponse>([
        GET_LIST_INTEGRATION_YOUTUBE_QUERY_KEY,
      ]);

      queryClient.setQueriesData<TGetListIntegrationYoutubeResponse>(
        { queryKey: [GET_LIST_INTEGRATION_YOUTUBE_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            payload: {
              ...oldData.payload,
              rows: oldData.payload.rows.filter(
                (integration) => integration.organizationChannelId !== data.organizationChannelId
              ),
            },
          };
        }
      );

      return { previousData } as TDeleteIntegrationYoutubeOptimisticallyContext;
    },

    onError: (_err, _data, context) => {
      if ((context as TDeleteIntegrationYoutubeOptimisticallyContext)?.previousData !== undefined) {
        queryClient.setQueryData(
          [GET_LIST_INTEGRATION_YOUTUBE_QUERY_KEY],
          (context as TDeleteIntegrationYoutubeOptimisticallyContext).previousData
        );
      }
    },

    ...restConfig,
  });
};
